import { Button, ButtonProps, Flex, Group, Modal, Stack, Text, Title } from '@mantine/core'
import { useTranslations } from 'next-intl'

type Props = {
  opened: boolean
  onClose: (e?: React.MouseEvent) => void
  title: string
  children: React.ReactNode
  closeButtonLabel: string
  confirmButtonColor?: ButtonProps['color']
  confirmButtonVariant?: ButtonProps['variant']
  onConfirm?: (e?: React.MouseEvent) => void
  loading?: boolean
}

export const ConfirmationModal: React.FC<Props> = ({
  opened,
  onClose,
  title,
  children,
  closeButtonLabel,
  confirmButtonColor = '',
  confirmButtonVariant = 'filled',
  onConfirm,
  loading = false,
}) => {
  const t = useTranslations('common')

  const handleClose = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    onClose(e)
  }

  const handleConfirm = (e?: React.MouseEvent) => {
    e?.preventDefault()
    e?.stopPropagation()
    onConfirm?.(e)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey && onConfirm && !loading) {
      e.preventDefault()
      e.stopPropagation()
      onConfirm()
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      centered
      padding="md"
      withCloseButton={false}
      onKeyDown={handleKeyDown}
      zIndex={10000}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={3}>{title}</Title>
          <Modal.CloseButton onClick={handleClose} />
        </Group>
        {typeof children === 'string' ? <Text size="sm">{children}</Text> : children}
        <Flex justify="space-between" mt="md">
          <Button variant="default" onClick={handleClose}>
            {closeButtonLabel}
          </Button>
          {onConfirm && (
            <Button variant={confirmButtonVariant} color={confirmButtonColor} onClick={handleConfirm} loading={loading}>
              {t('confirm')}
            </Button>
          )}
        </Flex>
      </Stack>
    </Modal>
  )
}
